import { Injectable } from '@angular/core';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { getTextIdByApplicationLanguageType } from '@fi-shared/fi-content-language-shared/get-tex-id-by-application-language-type';
import { AppTextService } from '@fi-shared/app-text';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import {
    IContentLanguage,
    IContentLanguageLabelModel,
    IPrioritizedContentLanguageLabelValueParams,
} from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { isDefined } from '@fi-shared/utils';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { createMap } from '@fi-shared/utils/create-map';

@Injectable()
export class FiContentLanguageSharedService {
    constructor(
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private appTextService: AppTextService,
    ) {}

    getContentLanguages() {
        const contentLanguages: IContentLanguage[] = [];
        this.customerDataService.getCustomerData().ContentLanguages.forEach((item) => {
            contentLanguages.push({
                Id: item.Id,
                IsDefaultLanguage: item.IsDefaultLanguage,
                Name: this.appTextService.get(getTextIdByApplicationLanguageType(item.Id)),
                SortIndex: item.SortIndex,
            });
        });
        return contentLanguages;
    }

    getApplicationLanguages() {
        const applicationLanguages: IContentLanguage[] =
            this.customerDataService.customerData.ApplicationLanguageAccess.map((applicationLanguageType) => ({
                Id: applicationLanguageType,
                Name: this.appTextService.get(getTextIdByApplicationLanguageType(applicationLanguageType)),
                IsDefaultLanguage:
                    applicationLanguageType === this.customerDataService.customerData.DefaultApplicationLanguageType,
                SortIndex: undefined,
            }));
        return applicationLanguages;
    }

    getDefaultContentLanguage() {
        return this.getContentLanguages().find((lang) => lang.IsDefaultLanguage);
    }

    getDefaultContentLanguageId() {
        const defaultLanguage = this.getDefaultContentLanguage();
        return defaultLanguage ? defaultLanguage.Id : undefined;
    }

    getContentLanguageIdByFilledModels(contentLanguageLabelModels: IContentLanguageLabelModel[]): number {
        const definedNames = contentLanguageLabelModels.filter((name) => name.Value?.trim().length > 0);
        const preferredContentLanguageId = this.userDataService.getContentLanguage();

        if (!definedNames.length) {
            return preferredContentLanguageId;
        }

        const isDefaultLanguageDefined = !!definedNames
            .find((name) => name.ContentLanguageId === preferredContentLanguageId)
            ?.Value?.trim().length;

        return isDefaultLanguageDefined ? preferredContentLanguageId : definedNames[0].ContentLanguageId;
    }

    getContentLanguageIdForDisplayContent(contents: IContentLanguageLabelModel[]): number {
        const contentLanguages = this.getContentLanguages();
        const notEmptyContents = this.getSortedNotEmptyContentLanguageLabels(contents);
        return notEmptyContents.length
            ? notEmptyContents[0].ContentLanguageId
            : this.userDataService.getContentLanguage();
    }

    getContentLanguageValueForDisplayContent(
        contents: IContentLanguageLabelModel[],
        selectedContentLanguageId?: FiApplicationLanguageType,
    ): string {
        const notEmptyContents = this.getSortedNotEmptyContentLanguageLabels(contents, selectedContentLanguageId);
        return notEmptyContents.length ? notEmptyContents[0].Value : '';
    }

    private getSortedNotEmptyContentLanguageLabels(
        contents: IContentLanguageLabelModel[],
        selectedContentLanguageId?: FiApplicationLanguageType,
    ) {
        const contentLanguages = this.getContentLanguages();
        const preferredContentLanguageId = this.userDataService.getContentLanguage();
        const notEmptyContents = contents
            .filter((content) => content.Value)
            .sort(
                comparator((item: IContentLanguageLabelModel) => {
                    if (item.ContentLanguageId === selectedContentLanguageId) {
                        return -2;
                    }
                    if (item.ContentLanguageId === preferredContentLanguageId) {
                        return -1;
                    }
                    const currentLanguage = contentLanguages.find((lang) => lang.Id === item.ContentLanguageId);
                    return currentLanguage?.SortIndex ?? 999;
                }),
            );
        return notEmptyContents;
    }

    /**
     * roles, ares, business areas and сlusters use system languages for its names and descriptions
     */
    initializeContentLanguagesArray(
        arr: IContentLanguageLabelModel[],
        data: IContentLanguageLabelModel[] = [],
        useApplicationLanguages: boolean = false,
    ) {
        const contentLanguages: IContentLanguage[] = !useApplicationLanguages
            ? this.customerDataService.customerData.ContentLanguages
            : this.getApplicationLanguages();

        const dataMap = createMap(data, 'ContentLanguageId');
        arr.length = 0; // https://chainformation.atlassian.net/browse/FI-2951
        contentLanguages.forEach((contentLanguage: IContentLanguage, i: number) => {
            arr.push(
                this.getContentLanguageModelObject(
                    contentLanguages[i].Id,
                    dataMap.hasOwnProperty(contentLanguages[i].Id) ? dataMap[contentLanguages[i].Id].Value : '',
                    contentLanguages[i].SortIndex,
                ),
            );
        });
    }

    private getCopyTranslationByLangId(langId: FiApplicationLanguageType): string {
        if (langId === FiApplicationLanguageType.Swedish) {
            return 'Kopia';
        }
        return 'Copy';
    }

    insertCopyLabelIntoContentLanguageLabelModels(
        contentLanguageLabelModels: IContentLanguageLabelModel[],
    ): IContentLanguageLabelModel[] {
        return contentLanguageLabelModels.map((model) => {
            if (!model.Value?.trim().length) {
                return model;
            }
            model.Value = `(${this.getCopyTranslationByLangId(model.ContentLanguageId)}) ` + model.Value;
            return model;
        });
    }

    getContentLanguageLabelModels(
        existingContentLanguageLabelModels?: IContentLanguageLabelModel[],
        useApplicationLanguages: boolean = false,
    ): IContentLanguageLabelModel[] {
        const langLabelModels: IContentLanguageLabelModel[] = [];
        this.initializeContentLanguagesArray(
            langLabelModels,
            existingContentLanguageLabelModels ?? [],
            useApplicationLanguages,
        );
        return langLabelModels;
    }

    private getContentLanguageModelObject(
        contentLanguageId: any,
        value: string,
        sortIndex: number,
    ): IContentLanguageLabelModel {
        return {
            ContentLanguageId: contentLanguageId,
            Value: value,
            SortIndex: sortIndex,
        };
    }

    getPrioritizedContentLanguageLabelValue(params: IPrioritizedContentLanguageLabelValueParams): string {
        const contentLanguageLabelModel: IContentLanguageLabelModel = params.contentLanguageLabelModels
            .sort(comparator((name) => name.SortIndex))
            .filter((name) => {
                return name.ContentLanguageId !== params.currentSelectedLanguageId;
            })
            .find((name) => name.Value);
        if (isDefined(contentLanguageLabelModel) && params.isLanguageNameShown) {
            return (
                contentLanguageLabelModel.Value +
                ' (' +
                this.appTextService.get(
                    getTextIdByApplicationLanguageType(contentLanguageLabelModel.ContentLanguageId),
                ) +
                ')'
            );
        }
        return contentLanguageLabelModel?.Value;
    }

    getAllApplicationLanguages() {
        const allApplicationLanguages: INameIdObj[] = [];
        for (let prop in FiApplicationLanguageType) {
            const isValueProperty = Number(prop) >= 0;
            if (isValueProperty) {
                allApplicationLanguages.push({
                    Id: Number(prop),
                    Name: FiApplicationLanguageType[prop],
                });
            }
        }
        return allApplicationLanguages;
    }

    getContentLanguageIdByContentTranslation(contentNames: IContentLanguageLabelModel[]): number {
        // todo we should also consider SortIndex
        // if this will work correct - replace getContentLanguageIdByContentTranslation by getContentLanguageIdByContentTranslation
        return this.getContentLanguageIdForDisplayContent(contentNames);
        /*const translations = contentNames.filter((name) => name.Value);
        const userPreferredContentLang = this.userDataService.getContentLanguage();
        const isPreferredContentLangTranslationPresent = !!translations.find((translation) => {
            return translation.ContentLanguageId === userPreferredContentLang;
        });

        return isPreferredContentLangTranslationPresent ? userPreferredContentLang :
            translations.find((translation) => translation.Value)?.ContentLanguageId;*/
    }
}
